import { cloneDeep, isArray } from "lodash-es";
import moment from "moment-timezone";
import React, { FC, ReactElement } from "react";

import { NativeSelect, OutlinedInput, Stack } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import { capitalize } from "@mui/material/utils";

import MultiValueTextFieldGroup from "../../../../components/MultiValueTextFieldGroup";
import TextField from "../../../../components/styled/TextField";
import { PplRule } from "../../../../store/pomeriumApi";
import clientCertificateFingerprint from "../../../../validators/clientCertificateFingerprint";
import clientCertificateSpkiHash from "../../../../validators/clientCertificateSpkiHash";
import PPLValue from "../PPLValue";
import UserIDSelector from "./UserIDSelector";

interface RuleFieldsProps {
  readonly: boolean;
  pplArr: any[];
  savePPL(ppl: PplRule[]): void;
  condition: "and" | "or" | "not" | "nor";
  blockIDX: number;
  allowDeny: "allow" | "deny";
  ruleIDX: number;
  criteria: string;
  operator: string;
  criteriaVal: any;
}

const RuleFields: FC<React.PropsWithChildren<RuleFieldsProps>> = ({
  readonly,
  pplArr,
  savePPL,
  condition,
  blockIDX,
  allowDeny,
  ruleIDX,
  criteria,
  operator,
  criteriaVal,
}: RuleFieldsProps): ReactElement => {
  const arr = cloneDeep(pplArr);
  const valueType = typeof criteriaVal;
  if (valueType === "string") {
    return (
      <PPLValue
        readonly={readonly}
        operator={operator}
        value={criteriaVal}
        onChange={(value: any) => {
          arr[blockIDX][allowDeny][condition][ruleIDX][criteria] = value;
          savePPL(arr);
        }}
      />
    );
  }

  switch (criteria) {
    case "date":
      return (
        <TextField
          label={capitalize(operator)}
          type="datetime-local"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={moment
            .utc(criteriaVal[operator])
            .local()
            .format("YYYY-MM-DDTHH:mm:ss")}
          onChange={(evt): void => {
            arr[blockIDX][allowDeny][condition][ruleIDX][criteria][operator] =
              moment(evt.target.value).utc().format("YYYY-MM-DDTHH:mm:ss");
            savePPL(arr);
          }}
          disabled={readonly}
        />
      );
    case "time_of_day":
      return (
        <Grid container spacing={0}>
          <Grid size={6}>
            <TextField
              style={{ width: "95%" }}
              label={capitalize(operator)}
              type="time"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={criteriaVal[operator]}
              onChange={(evt): void => {
                arr[blockIDX][allowDeny][condition][ruleIDX][criteria][
                  operator
                ] = evt.target.value;
                savePPL(arr);
              }}
              disabled={readonly}
            />
          </Grid>
          <Grid size={6}>
            <FormControl variant="outlined" sx={{ width: `95%` }}>
              <InputLabel
                htmlFor={
                  "outlined-timezone-native-simple" +
                  blockIDX +
                  allowDeny +
                  condition +
                  ruleIDX +
                  criteria +
                  operator +
                  "timezone"
                }
              >
                Timezone
              </InputLabel>
              <NativeSelect
                value={criteriaVal["timezone"]}
                onChange={(evt) => {
                  arr[blockIDX][allowDeny][condition][ruleIDX][criteria][
                    "timezone"
                  ] = evt.target.value;
                  savePPL(arr);
                }}
                size="small"
                input={<OutlinedInput label="Timezone" />}
                inputProps={{
                  name: "Timezone",
                  id:
                    "outlined-timezone-native-simple" +
                    blockIDX +
                    allowDeny +
                    condition +
                    ruleIDX +
                    criteria +
                    operator +
                    "timezone",
                }}
                disabled={readonly}
              >
                {moment.tz.names().map((timezone) => {
                  return (
                    <option
                      key={
                        blockIDX +
                        allowDeny +
                        condition +
                        ruleIDX +
                        criteria +
                        "timezone" +
                        timezone
                      }
                      value={timezone}
                    >
                      {timezone}
                    </option>
                  );
                })}
              </NativeSelect>
            </FormControl>
          </Grid>
        </Grid>
      );

    case "client_certificate":
      switch (operator) {
        case "fingerprint":
          return (
            <MultiValueTextFieldGroup
              fullWidth
              multiline
              key={
                blockIDX + allowDeny + condition + ruleIDX + criteria + operator
              }
              validator={clientCertificateFingerprint}
              disabled={readonly}
              variant="outlined"
              values={
                isArray(criteriaVal[operator])
                  ? criteriaVal[operator]
                  : [criteriaVal[operator]]
              }
              onSave={(toSave): void => {
                arr[blockIDX][allowDeny][condition][ruleIDX][criteria][
                  operator
                ] = toSave;
                savePPL(arr);
              }}
            />
          );
        case "spki_hash":
          return (
            <MultiValueTextFieldGroup
              fullWidth
              multiline
              key={
                blockIDX + allowDeny + condition + ruleIDX + criteria + operator
              }
              validator={clientCertificateSpkiHash}
              disabled={readonly}
              variant="outlined"
              values={
                isArray(criteriaVal[operator])
                  ? criteriaVal[operator]
                  : [criteriaVal[operator]]
              }
              onSave={(toSave): void => {
                arr[blockIDX][allowDeny][condition][ruleIDX][criteria][
                  operator
                ] = toSave;
                savePPL(arr);
              }}
            />
          );
        case "san_email":
        case "san_uri":
        case "san_dns":
        default: {
          const secondOperator = Object.keys(
            arr[blockIDX][allowDeny][condition][ruleIDX][criteria][operator],
          )[0];
          return (
            <Stack direction="row" spacing={1}>
              <FormControl variant="outlined" sx={{ width: `95%` }}>
                <InputLabel htmlFor="outlined-secondary-operator-native-simple">
                  Secondary Operator
                </InputLabel>
                <NativeSelect
                  value={secondOperator}
                  onChange={(evt) => {
                    const value = evt.target.value.toString();
                    const originalValue =
                      arr[blockIDX][allowDeny][condition][ruleIDX][criteria][
                        operator
                      ][secondOperator];
                    delete arr[blockIDX][allowDeny][condition][ruleIDX][
                      criteria
                    ][operator][secondOperator];
                    arr[blockIDX][allowDeny][condition][ruleIDX][criteria][
                      operator
                    ][value] = originalValue;
                    savePPL(arr);
                  }}
                  size="small"
                  input={<OutlinedInput label="Timezone" />}
                  inputProps={{
                    name: "Secondary Operator",
                    id:
                      "outlined-secondary-operator-native-simple" +
                      blockIDX +
                      allowDeny +
                      condition +
                      ruleIDX +
                      operator +
                      secondOperator,
                  }}
                  disabled={readonly}
                >
                  <option
                    key={
                      blockIDX +
                      allowDeny +
                      condition +
                      ruleIDX +
                      criteria +
                      operator +
                      "secondaryOperator_is"
                    }
                    value="is"
                  >
                    Is
                  </option>
                  <option
                    key={
                      blockIDX +
                      allowDeny +
                      condition +
                      ruleIDX +
                      criteria +
                      operator +
                      "secondaryOperator_contains"
                    }
                    value="contains"
                  >
                    Contains
                  </option>
                  <option
                    key={
                      blockIDX +
                      allowDeny +
                      condition +
                      ruleIDX +
                      criteria +
                      operator +
                      "secondaryOperator_ends_with"
                    }
                    value="ends_with"
                  >
                    Ends With
                  </option>
                  <option
                    key={
                      blockIDX +
                      allowDeny +
                      condition +
                      ruleIDX +
                      criteria +
                      operator +
                      "secondaryOperator_starts_with"
                    }
                    value="starts_with"
                  >
                    Starts With
                  </option>
                </NativeSelect>
              </FormControl>
              <TextField
                key={
                  blockIDX +
                  allowDeny +
                  condition +
                  ruleIDX +
                  criteria +
                  operator +
                  secondOperator +
                  "value"
                }
                style={{ width: "95%" }}
                label="Value"
                fullWidth
                value={criteriaVal[operator][secondOperator]}
                onChange={(evt): void => {
                  arr[blockIDX][allowDeny][condition][ruleIDX][criteria][
                    operator
                  ] = {
                    [secondOperator]: evt.target.value,
                  };
                  savePPL(arr);
                }}
                disabled={readonly}
              />
            </Stack>
          );
        }
      }
    case "user":
      return (
        <UserIDSelector
          readonly={readonly}
          id={
            blockIDX +
            allowDeny +
            condition +
            ruleIDX +
            criteria +
            "userCriteria"
          }
          value={criteriaVal[operator]}
          onChange={(value: string): void => {
            arr[blockIDX][allowDeny][condition][ruleIDX][criteria][operator] =
              value;
            savePPL(arr);
          }}
        />
      );
    default:
      return (
        <PPLValue
          readonly={readonly}
          operator={operator}
          value={criteriaVal[operator]}
          onChange={(value: any) => {
            arr[blockIDX][allowDeny][condition][ruleIDX][criteria][operator] =
              value;
            savePPL(arr);
          }}
        />
      );
  }
};

export default RuleFields;
